interface ApiError {
  msg: string;
  errors?: Record<string, string[]>;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
);

export const isApiError = (error: unknown): error is { data: ApiError } => (
  isRecord(error) && isRecord(error.data) && typeof error.data.msg === 'string'
);

/** ApiErrorConcern shape: 400 with field-level validation errors. */
export const isInvalidAttributesApiError = (error: unknown): boolean => (
  isApiError(error)
  && error.data.msg === 'invalid_attributes'
  && !!error.data.errors
  && Object.keys(error.data.errors).length > 0);

const getString = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim() ? value.trim() : undefined
);

const getErrorsMessage = (errors: unknown): string | undefined => {
  if (!isRecord(errors)) return undefined;

  const messages = Object.values(errors).flatMap((value) => {
    if (Array.isArray(value)) {
      return value.map(getString).filter((item): item is string => !!item);
    }

    const message = getString(value);
    return message ? [message] : [];
  });

  return messages.length > 0 ? messages.join('; ') : undefined;
};

const getMessageFromPayload = (payload: unknown): string | undefined => {
  const plainMessage = getString(payload);
  if (plainMessage) return plainMessage;
  if (!isRecord(payload)) return undefined;

  const msg = getString(payload.msg);
  const errorsMessage = getErrorsMessage(payload.errors);

  return (msg === 'invalid_attributes' ? errorsMessage : msg)
    || getString(payload.message)
    || errorsMessage
    || getString(payload.error);
};

/** Returns the user-facing detail available in RTK Query and standard errors. */
export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!isRecord(error)) return getString(error);

  return getMessageFromPayload(error.data)
    || getString(error.message)
    || getString(error.error);
};
